import { DCViewEnum } from '@shared/enums';
import {
  AggregationListResponse,
  DCTableAttribute,
  TADirectTypeEnum,
  TimelineListResponse,
} from '@shared/ui';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
} from 'rxjs';

import { Injectable, OnDestroy, inject, signal } from '@angular/core';

import { FeatureFlagsService } from '../../../shared/services';
import {
  SeenKeyTracker,
  ThemeService,
  UiHelpersService,
} from '../../../shared/ui/services';
import { RouterInput, RouterOutput } from '../../../utils';

@Injectable({
  providedIn: 'root',
})
export class DirectCollectService implements OnDestroy {
  uiHelper = inject(UiHelpersService);
  densityService = inject(ThemeService);
  featureFlagsService = inject(FeatureFlagsService);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    const view = localStorage.getItem('phoenixDCView') as DCViewEnum | null;
    const storedFilter = localStorage.getItem(
      'phoenixTableViewFilters'
    ) as DCViewEnum | null;

    if (view) {
      this.dcViewSignal.set(view);
    }

    if (storedFilter) {
      try {
        const filterValue = JSON.parse(storedFilter);
        if (Array.isArray(filterValue)) {
          this.dcTableViewFilter.next(filterValue);
        }
      } catch (e) {
        console.warn('Error parsing stored columns:', e);
      }
    }

    /** Subscription to keep filteredTimelineResults$ updated */
    combineLatest([this.timelineResults$, this.selectedSources$])
      .pipe(
        takeUntil(this.destroy$),
        map(([timelineResults, selectedSources]) => {
          if (selectedSources.length === 0) {
            return timelineResults;
          }
          const selectedSourceIds = new Set(
            selectedSources.map(s => s.id?.toLowerCase())
          );
          return timelineResults.filter(item => {
            const sourceName = item.collectionInfo?.sourceName?.toLowerCase();
            return sourceName != null && selectedSourceIds.has(sourceName);
          });
        })
      )
      .subscribe(filtered => this.filteredTimelineResults$.next(filtered));

    /** Subscription to sync missing sources from timeline to aggregation results */
    combineLatest([this.timelineResults$, this.aggregationResults$])
      .pipe(
        takeUntil(this.destroy$),
        filter(
          ([timeline, aggregations]: [
            TimelineListResponse[],
            AggregationListResponse[],
          ]) => timeline.length > 0 && aggregations.length > 0
        ),
        map(([timelineResults, aggregationResults]) => {
          const existingSourceIds = new Set(
            aggregationResults.map(a => a.id?.toLowerCase())
          );

          const missingSourceNames = new Set<string>();
          for (const item of timelineResults) {
            const sourceName = item.collectionInfo?.sourceName;
            if (
              sourceName &&
              !existingSourceIds.has(sourceName.toLowerCase())
            ) {
              missingSourceNames.add(sourceName);
            }
          }

          return Array.from(missingSourceNames);
        }),
        filter((missingSources: string[]) => missingSources.length > 0),
        distinctUntilChanged(
          (prev: string[], curr: string[]) =>
            prev.length === curr.length && prev.every((s, i) => s === curr[i])
        )
      )
      .subscribe(missingSources => {
        this.addMissingSourcesToAggregation(missingSources);
      });
  }

  /** Adds missing sources to aggregation results with zero counts */
  private addMissingSourcesToAggregation(sourceNames: string[]): void {
    const currentAggregations = this.aggregationResults$.value;
    const existingIds = new Set(
      currentAggregations.map(a => a.id?.toLowerCase())
    );

    const newSources: AggregationListResponse[] = sourceNames
      .filter(name => !existingIds.has(name.toLowerCase()))
      .map(
        name =>
          ({
            id: name,
            count: -1,
            clipboard: -1,
            message: -1,
            screenshot: -1,
            keylog: -1,
            file: -1,
            history: -1,
            windowList: -1,
          }) as AggregationListResponse
      );

    if (newSources.length > 0) {
      this.aggregationResults$.next([...currentAggregations, ...newSources]);
    }
  }

  public aggregationResults$ = new BehaviorSubject<AggregationListResponse[]>(
    []
  );
  private readonly timelineResults$ = new BehaviorSubject<
    TimelineListResponse[]
  >([]);
  public timelineSelectedItem$ = new BehaviorSubject<any>(null);
  public selectedSources$ = new BehaviorSubject<AggregationListResponse[]>([]);

  /** Timeline results filtered by selected sources (BehaviorSubject for synchronous access) */
  public filteredTimelineResults$ = new BehaviorSubject<TimelineListResponse[]>(
    []
  );

  public requestLoading$ = signal<boolean>(false);
  public itemLoading$ = signal<boolean>(false);
  public beforeActive$ = signal<boolean>(true);
  public afterActive$ = signal<boolean>(true);
  public loadingSourceMap$ = signal<boolean>(false);
  public showTranslations$ = signal<boolean>(false);
  public usedFilters$ = signal<
    RouterInput['directCollect']['aggregations']['filters']
  >({});
  dcViewSignal = signal<DCViewEnum>(DCViewEnum.LIST);
  defaultFilterValues: DCTableAttribute[] = [
    {
      v: 'icon',
    },
    {
      v: 'content',
    },
    {
      v: 'eventTime',
      t: 'date',
    },
    {
      v: 'collectionInfo.sourceName',
    },
  ];
  dcTableViewFilter = new BehaviorSubject<DCTableAttribute[]>([
    ...this.defaultFilterValues,
  ]);

  private readonly resultIds = new Set<string>();
  public duplicates = signal<boolean>(false);

  public screenshotDedupeEnabled = signal<boolean>(false);
  private readonly seenMd5 = new SeenKeyTracker();

  getExcludeMd5(): string[] {
    return this.seenMd5.toExcludeList();
  }

  setView(view: DCViewEnum) {
    localStorage.setItem('phoenixDCView', view);
    this.dcViewSignal.set(view);
  }

  set requestLoading(flag: boolean) {
    this.requestLoading$.set(flag);
  }

  set itemLoading(flag: boolean) {
    this.itemLoading$.set(flag);
  }

  set afterActive(flag: boolean) {
    this.afterActive$.set(flag);
  }

  set beforeActive(flag: boolean) {
    this.beforeActive$.set(flag);
  }

  set showTranslations(translate: boolean) {
    this.showTranslations$.set(translate);
  }

  set aggregationResults(
    results: RouterOutput['directCollect']['aggregations']['edges'][0][]
  ) {
    const values = this.aggregationResults$.value;
    values.push(
      ...results.map(item => item?.node).filter(node => node != null)
    );
    this.aggregationResults$.next(values);
  }

  set timelineResults(results: any[]) {
    this.addUniqueResults(results, this.timelineResults$);
  }

  set timelineBeforeResults(results: any[]) {
    this.addUniqueResults(results, this.timelineResults$, true);
  }

  selectedItemData(data: any): string {
    if (!data?.type) return '';

    const type = data.type
      .toLowerCase()
      .replace(/_(.)/g, (match: string, chr: string) => chr.toUpperCase());

    const highlightEnabled = this.featureFlagsService.isOn(
      'experiment_dcv_highlight'
    );

    switch (data.type) {
      case TADirectTypeEnum.HISTORY:
        if (highlightEnabled && data.highlight?.['history.page_title']) {
          return data.highlight['history.page_title'];
        }
        return data[type]?.pageTitle ?? '';

      case TADirectTypeEnum.TA_ACCOUNT:
        if (highlightEnabled && data.highlight?.['ta_account.notes']) {
          return data.highlight['ta_account.notes'];
        }
        return data[type]?.notes ?? '';

      case TADirectTypeEnum.CONVERSATION:
        return `${data[type]?.members?.length ?? 0} Participants </br> ${data[type]?.total ?? 0} Messages`;

      case TADirectTypeEnum.WINDOW_LIST:
        return (
          data[type]?.windows
            ?.map((window: { windowTitle?: string; text?: string }) =>
              [
                window.windowTitle
                  ? `<strong>${window.windowTitle}</strong>`
                  : '',
                window.text,
              ]
                .filter(Boolean)
                .join(': ')
            )
            .filter((line: string) => !!line)
            .join('\n') ?? ''
        );

      default:
        if (
          highlightEnabled &&
          data.highlight &&
          Object.keys(data.highlight).length > 0
        ) {
          return (
            data.highlight[`${type}.text`] ??
            data.highlight[`${type}.${type}`] ??
            data.highlight['message.text'] ??
            ''
          );
        }
        return data[type]?.text ?? data[type]?.[type] ?? '';
    }
  }

  /**
   * Due to the nature of Direct Collect Documents, sometimes more than one document has the exact same event  time
   * This causes the search to possibly find the same document multiple times
   * This function ensures that the same document is not added to the timeline more than  once
   */
  private addUniqueResults(
    results: any[],
    subject: BehaviorSubject<any[]>,
    prepend: boolean = false
  ) {
    const values = subject.value;
    const newResults = results
      .map(item => {
        return this.uiHelper.formatResponse(item);
      })
      .filter(item => {
        if (!item?.id) {
          return false;
        }

        if (this.resultIds.has(item.id)) {
          this.duplicates.update(() => true);
          return false;
        }

        if (this.screenshotDedupeEnabled()) {
          const md5 = item?.screenshot?.md5;
          if (md5) {
            if (this.seenMd5.has(md5)) {
              this.duplicates.update(() => true);
              return false;
            }
            this.seenMd5.add(md5);
          }
        }

        this.resultIds.add(item.id);
        return true;
      });

    if (prepend) {
      values.unshift(...newResults);
    } else {
      values.push(...newResults);
    }

    subject.next(values);
  }

  set loadingSourceMap(flag: boolean) {
    this.loadingSourceMap$.set(flag);
  }

  get usedFilters() {
    return this.usedFilters$();
  }

  set usedFilters(
    filters: RouterInput['directCollect']['aggregations']['filters']
  ) {
    this.usedFilters$.set(filters);
  }

  resetSearchResults(): void {
    this.itemLoading$.set(false);
    this.requestLoading$.set(false);
    this.loadingSourceMap$.set(false);
    this.aggregationResults$.next([]);
    this.timelineResults$.next([]);
    this.selectedSources$.next([]);
    this.timelineSelectedItem$.next(null);
    this.resultIds.clear();
    this.seenMd5.clear();
    this.duplicates.set(false);
  }

  set timelineSelectedItem(item: any) {
    this.timelineSelectedItem$.next(item);
  }

  set selectedSources(sources: AggregationListResponse[]) {
    this.selectedSources$.next(sources);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
